'use client';

/* eslint-disable eslint-compat/camelcase */
import { CategoryPreferences } from '@segment/consent-manager/types/types';
import { FeatureFlagsExtensions, ToolbarExtensions } from 'posthog-js/dist/extension-bundles';
import posthogClient, { PostHogConfig } from 'posthog-js/dist/module.slim';

import { POSTHOG_COOKIE_KEY } from '@pflegia/feature-flags/utils/constants';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';

import { accessTokenService } from '@shared/utils/access-token.service';

import { segmentService } from '../application/analytics/segment.service';
import { decidePosthogCapture } from './decide-posthog-capture';

type CustomPostHogConfig = PostHogConfig & {
  previousDistinctId?: string;
  fetchedAt?: number;
};

// function that returns the init object based on user consent
export const getConsentBasedProps = (cookiesSettings?: CategoryPreferences): Partial<CustomPostHogConfig> => {
  const analyticsApproved = Boolean(cookiesSettings?.marketingAndAnalytics);

  let bootstrapData;
  let previousDistinctId;
  let fetchedAt;
  if (analyticsApproved) {
    const cookieData = cookieStorage.getItem(POSTHOG_COOKIE_KEY);
    const bootstrappedData = JSON.parse(cookieData || '{}');

    ({ previousDistinctId, fetchedAt, ...bootstrapData } = bootstrappedData);
  }

  return {
    opt_out_capturing_by_default: !analyticsApproved,
    advanced_disable_feature_flags_on_first_load: !analyticsApproved,
    previousDistinctId,
    fetchedAt,
    ...(bootstrapData && { bootstrap: bootstrapData }),
  };
};

// add validation as per cookie consent
export const setupPostHogClient = (
  includeSegmentFeatures?: boolean,
  cookiesSettings?: CategoryPreferences,
  { sendPageView = true } = {},
) => {
  const nextPublicPosthogApiKey = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
  const nextPublicPosthogApiHost = process.env.NEXT_PUBLIC_POSTHOG_API_HOST;
  if (!nextPublicPosthogApiKey || !nextPublicPosthogApiHost || typeof window === 'undefined') {
    return posthogClient;
  }

  // the previousDistinctId is the id stored in PostHog cookie if it's different from the user id
  const { previousDistinctId, fetchedAt, ...consentBasedProps } = getConsentBasedProps(cookiesSettings);
  // the bootstrapped id is subsequently either the user id, the id stored in PostHog cookie, or the random ID generated during the initial load.
  const bootstrappedId = consentBasedProps.bootstrap?.distinctID;
  if (!bootstrappedId) return posthogClient;
  posthogClient.init(nextPublicPosthogApiKey, {
    __extensionClasses: {
      ...FeatureFlagsExtensions,
      ...(process.env.NEXT_PUBLIC_ENV !== 'production' && ToolbarExtensions),
    },
    ...consentBasedProps,
    api_host: nextPublicPosthogApiHost,
    // Disable capturing pageviews by PostHog as we consider only events sent from Segment.
    capture_pageview: false,
    loaded: phInstance => {
      // Enable debug mode in non-production environments
      if (process.env.NEXT_PUBLIC_ENV !== 'production') phInstance.debug();
    },
    // To ensure that anonymous events are saving person profiles and will save feature flag values when identified
    person_profiles: 'always',
    ...(includeSegmentFeatures && { segment: segmentService.core() }),
    ui_host: 'https://eu.posthog.com',
  });

  // Since segment is loaded after posthog, we had to move this out of loaded callback to ensure it is always called
  if (includeSegmentFeatures) {
    const accessTokenId = accessTokenService.getDecodedToken()?.sub;
    let segmentId = segmentService.getID();
    // We get the existing id from Segment or PostHog bootstrapped data
    const existingId = posthogClient.get_distinct_id();
    // We alias the user in posthog to the previous distinct id if it's different from the current user id

    // For anonymous users, we should set Segment's anonymousId to match PostHog's distinct_id
    if (!accessTokenId && existingId && existingId !== segmentId) {
      segmentService.setAnonymousId(existingId);
      segmentId = existingId;
    }

    [previousDistinctId, segmentId, bootstrappedId].filter(Boolean).forEach(id => {
      if (existingId !== id) {
        // @ts-expect-error: id should be defined as per filter
        posthogClient.alias(id);
      }
    });
    // We identify the user in posthog only when the user performs an authentication action.
    if (accessTokenId && accessTokenId !== existingId) {
      // we identify the user in posthog with either of the existing ids to merge the user data on PostHog
      posthogClient.identify(accessTokenId, {
        internal_id: accessTokenId,
      });
    }
    if (accessTokenId && accessTokenId !== segmentId) {
      segmentService.setAnonymousId(accessTokenId);
    }
    if (previousDistinctId && previousDistinctId !== existingId) {
      // And we'd use it as a one-off to recover the current user previous data
      cookieStorage.setItem(POSTHOG_COOKIE_KEY, JSON.stringify({ ...consentBasedProps.bootstrap, fetchedAt }));
    }
    // once the user is identified we can select further marketing details
    decidePosthogCapture(posthogClient, cookiesSettings, { sendPageView });
  }

  return posthogClient;
};
